import type { IntegrationType } from "@v2/features/settings/shop/integrations/IntegrationForm";
import type { ReploIntegration } from "schemas/generated/integration";

import * as React from "react";

import PageHeader from "@replo/design-system/blocks/page-header/PageHeader";
import { Button } from "@replo/design-system/components/button/Button";
import { Skeleton } from "@replo/design-system/components/skeleton/Skeleton";
import { skipToken } from "@tanstack/react-query";
import { INTEGRATION_CONFIGS } from "@v2/features/settings/shop/integrations/config";
import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";
import { integrationRouter } from "@v2/trpc/routes/integration";
import { routes } from "@v2/utils/router";
import { ChevronRight } from "lucide-react";
import { generatePath, useNavigate } from "react-router-dom";

// ---------------------------------------------------------------------------
// Row component — one per connected integration
// ---------------------------------------------------------------------------

const EnabledIntegrationRow: React.FC<{
  integration: ReploIntegration;
}> = ({ integration }) => {
  const navigate = useNavigate();
  const projectId = useCurrentProjectId();

  // Skip non-dashboard integration types
  if (
    integration.type === "ShopifyIntegrationConfig" ||
    integration.type === "StripeConnect"
  ) {
    return null;
  }

  const integrationType = integration.type as IntegrationType;
  const config = INTEGRATION_CONFIGS[integrationType];
  if (!config) {
    return null;
  }

  const connectedDate = new Date(integration.createdAt).toLocaleDateString(
    "en-US",
    { month: "short", year: "numeric" },
  );

  return (
    <button
      type="button"
      className="group/row flex items-center gap-3 px-4 py-3.5 w-full text-left cursor-pointer hover:bg-button-secondary-hover transition-colors"
      onClick={() => {
        navigate(
          generatePath(routes.shops.integrations.detail, {
            projectId: projectId ?? "",
            integrationType,
          }),
        );
      }}
    >
      <img
        src={config.image}
        alt={config.title}
        className="size-8 rounded-lg object-contain shrink-0"
      />
      <div className="flex flex-col gap-0.5 min-w-0 flex-1">
        <span className="text-sm font-medium text-default">{config.title}</span>
        <span className="text-xs text-muted">Enabled on {connectedDate}</span>
      </div>
      <Button
        icon
        variant="secondary"
        size="sm"
        tabIndex={-1}
        UNSAFE_className="opacity-0 group-hover/row:opacity-100 transition-opacity shrink-0"
      >
        <ChevronRight size={12} />
      </Button>
    </button>
  );
};

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export const EnabledIntegrationsPage: React.FC = () => {
  const projectId = useCurrentProjectId();
  const navigate = useNavigate();

  const { data: integrations, isLoading } = integrationRouter.find.useQuery(
    projectId ? { projectId } : skipToken,
  );

  const connectedIntegrations = React.useMemo(() => {
    return (integrations ?? []).filter((integration) => {
      return (
        !integration.deletedAt &&
        integration.type !== "ShopifyIntegrationConfig" &&
        integration.type !== "StripeConnect"
      );
    });
  }, [integrations]);

  const goBack = () => {
    navigate(
      generatePath(routes.shops.integrations.list, {
        projectId: projectId ?? "",
      }),
    );
  };

  if (isLoading) {
    return (
      <div className="flex flex-col w-full h-full max-w-[800px] mx-auto">
        <PageHeader title={<Skeleton className="h-8 w-64" />} />
        <div className="flex flex-col mt-6 rounded-xl border-0.5 border-border overflow-hidden">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="flex items-center gap-3 px-4 py-3.5">
              <Skeleton className="size-8 rounded-lg" />
              <div className="flex flex-col gap-1">
                <Skeleton className="h-4 w-28" />
                <Skeleton className="h-3 w-40" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col w-full h-full max-w-[800px] mx-auto">
      <PageHeader
        onBackClick={goBack}
        showDivider={false}
        title="Enabled integrations"
      />

      {connectedIntegrations.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <span className="text-sm text-muted">
            No integrations are enabled yet.
          </span>
        </div>
      ) : (
        <div className="flex flex-col mt-6 rounded-xl border-0.5 border-border overflow-hidden divide-y divide-dashed divide-border">
          {connectedIntegrations.map((integration) => (
            <EnabledIntegrationRow
              key={integration.id}
              integration={integration}
            />
          ))}
        </div>
      )}
    </div>
  );
};
